import React from "react";
import { useNavigate } from "react-router-dom";
import { EvaluateResponse, EvaluateResult } from "../../model_gen/chat";
import { Sentinel } from "../../model_gen/sentinel";
import { Button } from "../ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { MultiSelect } from "../ui/multi-select";
import { Badge } from "../ui/badge";
import { 
  PlayIcon,
  Loader2Icon,
  CheckCircleIcon,
  XCircleIcon,
  MinusCircleIcon,
  EyeIcon,
  ShieldCheckIcon,
  AlertTriangleIcon,
  ClockIcon
} from "lucide-react";
import { cn } from "../../lib/utils";

interface EvaluationBlockData {
  id: string;
  selectedSentinels: string[];
  editedSentinels: { [key: string]: Sentinel };
  versionId: string;
  hasRun: boolean;
  evaluationResponse?: EvaluateResponse;
  evaluationRunId?: number;
  evaluationRequestId?: string;
}

interface EvaluationBlockProps {
  block: EvaluationBlockData;
  originalSentinels: Sentinel[];
  selectKey: number;
  runningEvaluations: { [key: string]: boolean };
  companyId?: number;
  onSentinelChange: (value: (string | number)[], blockId: string) => void;
  onRunEvaluation: (blockId: string) => void;
  onViewRequest: (evaluationRequestId: string, sentinelId: string) => React.ReactNode;
  isEvaluationRunning: () => boolean;
}

const EvaluationBlock: React.FC<EvaluationBlockProps> = ({
  block,
  originalSentinels,
  selectKey,
  runningEvaluations,
  companyId,
  onSentinelChange,
  onRunEvaluation,
  onViewRequest,
  isEvaluationRunning,
}) => {
  const navigate = useNavigate();

  const getStatusBadge = () => {
    if (!block.hasRun) {
      return (
        <Badge variant="outline" className="border-orange-500 text-orange-700 bg-orange-50 dark:bg-orange-900/10 dark:text-orange-400 dark:border-orange-400">
          <AlertTriangleIcon className="w-3 h-3 mr-1" />
          Manual
        </Badge>
      );
    }

    if (block.evaluationRunId && block.evaluationRunId > 0) {
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/evaluation/${block.evaluationRunId}`);
          }}
          className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-gc-med bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400 hover:bg-purple-200 dark:hover:bg-purple-900/30 transition-colors border border-purple-200 dark:border-purple-800"
        >
          <ShieldCheckIcon className="w-3 h-3" />
          Batch Eval
        </button>
      );
    }

    if (block.evaluationRequestId?.startsWith("manual_")) {
      return (
        <Badge variant="outline" className="border-orange-500 text-orange-700 bg-orange-50 dark:bg-orange-900/10 dark:text-orange-400 dark:border-orange-400">
          <AlertTriangleIcon className="w-3 h-3 mr-1" />
          Manual
        </Badge>
      );
    }

    return (
      <Badge variant="outline" className="border-blue-500 text-blue-700 bg-blue-50 dark:bg-blue-900/10 dark:text-blue-400 dark:border-blue-400">
        <ClockIcon className="w-3 h-3 mr-1" />
        Real-time
      </Badge>
    );
  };

  const renderEvaluationResult = (evaluationResponse: EvaluateResponse) => {
    if (!evaluationResponse.evaluation_results || evaluationResponse.evaluation_results.length === 0) {
      return (
        <div className="flex items-center gap-2 px-3 py-1 bg-gray-50 dark:bg-gray-900/20 rounded-full">
          <span className="text-xs font-gc-reg text-muted-foreground">
            {evaluationResponse.evaluation_time}ms
          </span>
          <MinusCircleIcon className="w-4 h-4 text-muted-foreground" />
        </div>
      );
    }

    const hasFault = evaluationResponse.evaluation_results.some(
      (result: EvaluateResult) => result.status === "FAULT"
    );

    const passCount = evaluationResponse.evaluation_results.filter(r => r.status === "PASS").length;
    const faultCount = evaluationResponse.evaluation_results.filter(r => r.status === "FAULT").length;
    const total = evaluationResponse.evaluation_results.length;

    return (
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 px-3 py-1 bg-gray-50 dark:bg-gray-900/20 rounded-full">
          <span className="text-xs font-gc-reg text-muted-foreground">
            {evaluationResponse.evaluation_time}ms
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className={cn(
            "flex items-center gap-1 px-2 py-1 rounded-full text-xs font-gc-med",
            hasFault 
              ? "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400"
              : "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
          )}>
            {hasFault ? (
              <XCircleIcon className="w-3 h-3" />
            ) : (
              <CheckCircleIcon className="w-3 h-3" />
            )}
            <span>{passCount}/{total}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderResultCard = (result: EvaluateResult, index: number) => {
    const getStatusColor = (status: string) => {
      switch (status) {
        case "FAULT":
          return "border-l-red-500 bg-red-50/50 dark:bg-red-900/5";
        case "PASS":
          return "border-l-green-500 bg-green-50/50 dark:bg-green-900/5";
        case "NA":
          return "border-l-gray-400 bg-gray-50/50 dark:bg-gray-900/5";
        default:
          return "border-l-blue-500 bg-blue-50/50 dark:bg-blue-900/5";
      }
    };

    const getStatusBadge = (status: string) => {
      switch (status) {
        case "FAULT":
          return "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400";
        case "PASS":
          return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
        case "NA":
          return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
        default:
          return "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400";
      }
    };

    return (
      <div
        key={index}
        className={cn(
          "border-l-4 rounded-r-lg p-4 border border-border",
          getStatusColor(result.status)
        )}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Badge className={cn("text-xs font-gc-med", getStatusBadge(result.status))}>
              {result.sentinel_name}
            </Badge>
            <div className="flex items-center gap-1 text-xs text-muted-foreground font-gc-reg">
              <ClockIcon className="w-3 h-3" />
              {result.eval_time}ms
            </div>
          </div>
          {companyId === 2 && (
            <div>
              {onViewRequest(
                block.evaluationResponse!.evaluation_request_id.toString(),
                result.sentinel_id.toString()
              )}
            </div>
          )}
        </div>
        
        {result.description && (
          <p className="text-sm text-foreground mb-4 font-gc-reg leading-relaxed">
            {result.description}
          </p>
        )}
        
        {result.status === "FAULT" && result.meta?.correction && (
          <div className="border-l-4 border-blue-500 bg-blue-50/50 dark:bg-blue-900/10 pl-4 py-3 rounded-r">
            <div className="flex items-start gap-2">
              <div className="text-blue-600 dark:text-blue-400 mt-0.5">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="text-xs font-gc-med text-blue-800 dark:text-blue-300 mb-1">
                  Suggested Correction
                </p>
                <p className="text-sm italic text-blue-700 dark:text-blue-300 font-gc-reg">
                  {result.meta.correction as string}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Accordion type="single" collapsible className="w-full">
      <AccordionItem value={block.id} className="border border-border rounded-lg shadow-sm">
        <AccordionTrigger className="px-6 py-5 hover:no-underline hover:bg-accent/20 transition-colors rounded-t-lg">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-4">
              {getStatusBadge()}
              
              <div className="flex items-center gap-3">
                <MultiSelect
                  key={selectKey}
                  options={originalSentinels.map((sentinel) => ({
                    label: sentinel.sentinel_name,
                    value: sentinel.id.toString(),
                  }))}
                  value={block.selectedSentinels}
                  onChange={(value) => onSentinelChange(value, block.id)}
                  placeholder="Select Sentinels"
                  disabled={block.evaluationResponse !== undefined}
                  className="min-w-[200px]"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {block.evaluationResponse ? (
                renderEvaluationResult(block.evaluationResponse)
              ) : (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRunEvaluation(block.id);
                  }}
                  disabled={
                    block.selectedSentinels.length === 0 ||
                    runningEvaluations[block.id] ||
                    isEvaluationRunning()
                  }
                  className="gap-2 bg-maitai-green hover:bg-maitai-green/90 text-white"
                >
                  {runningEvaluations[block.id] ? (
                    <Loader2Icon className="w-4 h-4 animate-spin" />
                  ) : (
                    <PlayIcon className="w-4 h-4" />
                  )}
                  {runningEvaluations[block.id] ? "Running..." : "Run Evaluation"}
                </Button>
              )}
            </div>
          </div>
        </AccordionTrigger>
        
        <AccordionContent className="px-6 pb-6">
          {block.evaluationResponse && (
            <div className="space-y-4 pt-2">
              {block.evaluationResponse.evaluation_results.map((result, index) =>
                renderResultCard(result, index)
              )}
            </div>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default EvaluationBlock; 